/**
 * AI Action Plan Alignment Dashboard — maps tenant posture to the three
 * pillars of America's AI Action Plan plus 10 posture cards.
 */

const CARD_DEFS = [
  { key: 'ai_adoption_readiness_score', label: 'AI Adoption Readiness', suffix: '%', nav: 'ai-readiness' },
  { key: 'ai_evaluation_maturity', label: 'AI Evaluation Maturity', suffix: '%', nav: 'ai-evaluations' },
  { key: 'secure_by_design_control_status', label: 'Secure-by-Design Controls', suffix: '%', nav: 'ai-controls' },
  { key: 'ai_incident_response_readiness', label: 'AI Incident Readiness', suffix: '%', nav: 'incidents' },
  { key: 'workforce_training_completion', label: 'Workforce Training', suffix: '%', nav: 'training' },
  { key: 'evidence_completeness', label: 'Evidence Completeness', suffix: '%', nav: 'ai-evaluations' },
  { key: 'open_remediation_actions', label: 'Open Remediation Actions', suffix: '', nav: 'incidents' },
  { key: 'high_risk_ai_systems', label: 'High-Risk AI Systems', suffix: '', nav: 'ai-assets' },
  { key: 'ai_systems_pending_approval', label: 'AI Systems Pending Approval', suffix: '', nav: 'ai-assets' },
];

function pillarClass(status) {
  return status === 'on_track' ? 'success' : status === 'needs_attention' ? 'warning' : 'critical';
}

export const actionPlan = {
  async actionPlan() {
    let res = { data: null };
    try { res = await API.getActionPlanDashboard(); } catch (e) { /* defaults */ }
    const d = res.data;

    if (!d) {
      return `
        <div class="page-header"><div><h2>AI Action Plan Alignment</h2>
        <p>Posture mapped to America's AI Action Plan</p></div></div>
        <div class="empty-state"><div class="empty-icon">&#9673;</div>
        <p>Unable to load alignment data. Register AI systems and run evaluations to populate this dashboard.</p></div>`;
    }

    const cards = d.cards;
    const origin = cards.vendor_model_origin_risk || { high: 0, moderate: 0, low: 0 };

    const pillarCards = d.pillars.map(p => `
      <div class="stat-card ${pillarClass(p.status)}">
        <div class="stat-value">${p.score}%</div>
        <div class="stat-label">${p.label}</div>
        <div style="margin-top:8px">${App.badge(p.status)}</div>
      </div>`).join('');

    const postureCards = CARD_DEFS.map(c => `
      <div class="stat-card clickable" data-action="App.navigate" data-a0="${c.nav}" role="button" tabindex="0" title="Open ${c.label}">
        <div class="stat-value">${cards[c.key] ?? 0}${c.suffix}</div>
        <div class="stat-label">${c.label}</div>
      </div>`).join('');

    return `
      <div class="page-header">
        <div><h2>AI Action Plan Alignment</h2>
        <p>Governance posture mapped to America's AI Action Plan — maturity: <strong>${App.formatLabel(d.maturity_level)}</strong></p></div>
      </div>

      <h3 style="margin:8px 0 12px">Action Plan Pillars</h3>
      <div class="stats-row">${pillarCards}</div>

      <h3 style="margin:24px 0 12px">Posture Cards</h3>
      <div class="stats-row" style="flex-wrap:wrap">${postureCards}</div>

      <div class="card" style="margin-top:24px">
        <div class="card-header"><h3>Vendor / Model Origin Risk</h3></div>
        <div class="stats-row">
          <div class="stat-card critical"><div class="stat-value">${origin.high}</div><div class="stat-label">High Origin Risk</div></div>
          <div class="stat-card warning"><div class="stat-value">${origin.moderate}</div><div class="stat-label">Moderate Origin Risk</div></div>
          <div class="stat-card success"><div class="stat-value">${origin.low}</div><div class="stat-label">Low Origin Risk</div></div>
        </div>
      </div>

      <div class="card" style="margin-top:24px">
        <div class="card-header"><h3>Portfolio Totals</h3></div>
        <div class="stats-row">
          <div class="stat-card info"><div class="stat-value">${d.totals.ai_systems}</div><div class="stat-label">AI Systems</div></div>
          <div class="stat-card info"><div class="stat-value">${d.totals.evaluations}</div><div class="stat-label">Evaluations</div></div>
          <div class="stat-card success"><div class="stat-value">${d.totals.approved_evaluations}</div><div class="stat-label">Approved Evaluations</div></div>
          <div class="stat-card warning"><div class="stat-value">${d.totals.open_incidents}</div><div class="stat-label">Open Incidents</div></div>
        </div>
      </div>`;
  },
};
